import type {
  OrgTemplates,
  OrgTemplate,
  WorkingAssignments,
  OrgTemplatesFilters,
} from 'src/types/scheduler/org-templates';
import type {Assignments} from 'src/types/scheduler/templates';

import {camel, snake} from 'src/utils';
import * as reduxApi from 'src/utils/redux-api-v2';
import type {Dispatch, ThunkAction, GetState} from 'src/reducers';

import {selectOrgTemplatesFilters} from 'src/selectors/scheduler/org-templates';
import {setTemplateForm} from 'src/action-creators/scheduler/template-form';


export const SET_ORG_TEMPLATES = 'scheduling/org-templates/setOrgTemplates';
export const ADD_ORG_TEMPLATE = 'scheduling/org-templates/addOrgTemplate';
export const REMOVE_ORG_TEMPLATE = 'scheduling/org-templates/removeOrgTemplate';
export const UPDATE_ORG_TEMPLATE = 'scheduling/org-templates/updateOrgTemplate';
export const SET_ORG_TEMPLATES_FILTERS =
  'scheduling/org-templates/setOrgTemplatesFilters';
export const SET_ORG_TEMPLATE_ASSIGNMENTS =
  'scheduling/org-templates/setOrgTemplateAssignments';

export type SchedulerOrgTemplateActions =
  | SetOrgTemplatesAction
  | AddOrgTemplateAction
  | RemoveOrgTemplateAction
  | UpdateOrgTemplateAction
  | SetOrgTemplatesFiltersAction
  | SetOrgTemplateAssignmentsAction;

// set org templates
export type SetOrgTemplatesAction = {
  type: typeof SET_ORG_TEMPLATES;
  payload: OrgTemplates;
};
export const setOrgTemplates = (
  payload: OrgTemplates,
): SetOrgTemplatesAction => ({
  type: SET_ORG_TEMPLATES,
  payload,
});

// add org template
export type AddOrgTemplateAction = {
  type: typeof ADD_ORG_TEMPLATE;
  payload: OrgTemplate;
};
export const addOrgTemplate = (payload: OrgTemplate): AddOrgTemplateAction => ({
  type: ADD_ORG_TEMPLATE,
  payload,
});

// remove org template
export type RemoveOrgTemplateAction = {
  type: typeof REMOVE_ORG_TEMPLATE;
  payload: string;
};
export const removeOrgTemplate = (
  payload: string,
): RemoveOrgTemplateAction => ({
  type: REMOVE_ORG_TEMPLATE,
  payload,
});

// update org template
type UpdateOrgTemplatePayload = {
  templateId: string;
  payload: OrgTemplate;
};
export type UpdateOrgTemplateAction = {
  type: typeof UPDATE_ORG_TEMPLATE;
  payload: UpdateOrgTemplatePayload;
};
export const updateOrgTemplate = (
  payload: UpdateOrgTemplatePayload,
): UpdateOrgTemplateAction => ({
  type: UPDATE_ORG_TEMPLATE,
  payload,
});

// set org templates filters
export type SetOrgTemplatesFiltersAction = {
  type: typeof SET_ORG_TEMPLATES_FILTERS;
  payload: OrgTemplatesFilters;
};
export const setOrgTemplatesFilters = (
  payload: OrgTemplatesFilters,
): SetOrgTemplatesFiltersAction => ({
  type: SET_ORG_TEMPLATES_FILTERS,
  payload,
});

// Set org template assignments
type SetOrgTemplateAssignmentsPayload = {
  templateId: string;
  assignments: Assignments;
};
export type SetOrgTemplateAssignmentsAction = {
  type: typeof SET_ORG_TEMPLATE_ASSIGNMENTS;
  payload: SetOrgTemplateAssignmentsPayload;
};
export const setOrgTemplateAssignments = (
  payload: SetOrgTemplateAssignmentsPayload,
): SetOrgTemplateAssignmentsAction => ({
  type: SET_ORG_TEMPLATE_ASSIGNMENTS,
  payload,
});

export const fetchOrgTemplates =
  (): ThunkAction => async (dispatch: Dispatch, getState: GetState) => {
    const filters = selectOrgTemplatesFilters(getState());
    try {
      const orgTemplates: OrgTemplates = await dispatch(
        reduxApi.get('scheduler/organisational-templates', {
          name: filters.name,
        }),
      );

      const normalisedOrgTemplates = camel(orgTemplates, true, [
        'blocks',
        'entityMap',
      ]);

      dispatch(setOrgTemplates(normalisedOrgTemplates));
    } catch (error: any) {
      throw error;
    }
  };

export const fetchOrgTemplate =
  (templateId: string): ThunkAction =>
  async (dispatch: Dispatch) => {
    try {
      const result = await dispatch(
        reduxApi.get(`scheduler/organisational-template/${templateId}`),
      );

      const camelizedResult = camel(result, true, ['blocks', 'entityMap']);
      const {assignments} = camelizedResult;
      const workingAssignments = {
        isAssignedToAll: assignments.isAssignedToAll,
        inclusions: {
          agentIds: [],
          securityGroupIds: [],
        },
        exclusions: {
          agentIds: [],
          securityGroupIds: [],
        },
      } as const;
      dispatch(setTemplateForm({...camelizedResult, workingAssignments}));
    } catch (err: any) {
      throw err;
    }
  };

export const createOrgTemplate =
  (): ThunkAction => async (dispatch: Dispatch) => {
    try {
      const orgTemplate: OrgTemplate = await dispatch(
        reduxApi.post('scheduler/organisational-templates', {
          distribution_rule: 'single_account',
        }),
      );

      const normalisedOrgTemplate = camel(orgTemplate, true, [
        'blocks',
        'entityMap',
      ]);

      dispatch(addOrgTemplate(normalisedOrgTemplate));
      return normalisedOrgTemplate;
    } catch (error: any) {
      throw error;
    }
  };

export const deleteOrgTemplate =
  (templateId: string): ThunkAction =>
  async (dispatch: Dispatch) => {
    try {
      await dispatch(
        reduxApi.del(`scheduler/organisational-template/${templateId}`),
      );

      dispatch(removeOrgTemplate(templateId));
    } catch (error: any) {
      throw error;
    }
  };

export const copyOrgTemplate =
  (templateId: string): ThunkAction =>
  async (dispatch: Dispatch) => {
    try {
      const orgTemplate: OrgTemplate = await dispatch(
        reduxApi.post(`scheduler/organisational-template/${templateId}/copy`, {
          include_assignments: true,
        }),
      );

      const normalisedOrgTemplate = camel(orgTemplate, true, [
        'blocks',
        'entityMap',
      ]);

      dispatch(addOrgTemplate(normalisedOrgTemplate));
      return normalisedOrgTemplate;
    } catch (error: any) {
      throw error;
    }
  };

export const updateOrgTemplateAssignments =
  (templateId: string, assignments: Assignments): ThunkAction =>
  async (dispatch: Dispatch) => {
    try {
      dispatch(
        setOrgTemplateAssignments({
          templateId,
          assignments,
        }),
      );
    } catch (error: any) {
      throw error;
    }
  };

// Extra API calls for org templates
export const saveWorkingAssignments =
  (templateId: string, assignments: WorkingAssignments): ThunkAction =>
  async (dispatch: Dispatch) => {
    try {
      const result = await dispatch(
        reduxApi.post(
          `scheduler/organisational-template/${templateId}/assignments`,
          snake(assignments),
        ),
      );

      // update the redux state
      dispatch(updateOrgTemplateAssignments(templateId, camel(result)));
    } catch (error: any) {
      throw error;
    }
  };
